'use client';

import { useMemo } from 'react';
import type { CSSProperties } from 'react';
import { useTheme } from '@/contexts/ThemeContext';

const TICK_FONT_SIZE = 11;

export interface ChartPalette {
  isDark: boolean;
  /** CartesianGrid `stroke` */
  gridStroke: string;
  /** CartesianGrid `opacity` */
  gridOpacity: number;
  /** Shared XAxis / YAxis tick props */
  axisTick: { fill: string; fontSize: number };
  tooltipContentStyle: CSSProperties;
  tooltipLabelStyle: CSSProperties;
  tooltipItemStyle: CSSProperties;
  /** Stacked / muted series (e.g. Absent vs Present) */
  barMutedFill: string;
  /** RadialBar / gauge track behind the filled arc */
  radialTrackFill: string;
}

/**
 * Recharts tokens aligned with ThemeContext light/dark mode.
 * Use in dashboards so CartesianGrid, axes, tooltips, and muted bars stay readable on both backgrounds.
 */
export function useChartPalette(): ChartPalette {
  const { isDark } = useTheme();

  return useMemo((): ChartPalette => {
    if (isDark) {
      return {
        isDark: true,
        gridStroke: '#4b5563',
        gridOpacity: 0.35,
        axisTick: { fill: '#9ca3af', fontSize: TICK_FONT_SIZE },
        tooltipContentStyle: {
          backgroundColor: '#1f2937',
          border: '1px solid #374151',
          borderRadius: 8,
          fontSize: 12,
          color: '#f3f4f6',
        },
        tooltipLabelStyle: { color: '#e5e7eb' },
        tooltipItemStyle: { color: '#f9fafb' },
        barMutedFill: '#6b7280',
        radialTrackFill: '#374151',
      };
    }

    return {
      isDark: false,
      gridStroke: '#d1d5db',
      gridOpacity: 0.55,
      axisTick: { fill: '#6b7280', fontSize: TICK_FONT_SIZE },
      tooltipContentStyle: {
        backgroundColor: '#ffffff',
        border: '1px solid #e5e7eb',
        borderRadius: 8,
        fontSize: 12,
        color: '#111827',
        boxShadow:
          '0 10px 15px -3px rgb(0 0 0 / 0.08), 0 4px 6px -4px rgb(0 0 0 / 0.08)',
      },
      tooltipLabelStyle: { color: '#374151' },
      tooltipItemStyle: { color: '#111827' },
      barMutedFill: '#d1d5db',
      radialTrackFill: '#e5e7eb',
    };
  }, [isDark]);
}
